import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);

    // In production, you might want to log this to an error tracking service
    // e.g., Sentry, LogRocket, etc.
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-black p-6 text-white">
          <div className="w-full max-w-md border border-white/20 p-8">
            <h1 className="mb-4 font-serif text-2xl tracking-tighter uppercase">
              Something went wrong
            </h1>
            <p className="mb-6 text-sm text-white/60">
              We encountered an unexpected error. Please try refreshing the page.
            </p>
            {this.state.error && (
              <details className="mb-6 text-xs text-white/40">
                <summary className="mb-2 cursor-pointer transition-colors hover:text-white/60">
                  Error details
                </summary>
                <pre className="overflow-auto rounded bg-white/5 p-4">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            <button
              onClick={() => window.location.reload()}
              className="w-full border border-white px-6 py-3 text-sm tracking-widest uppercase transition-colors hover:bg-white hover:text-black"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
